import React, { useState } from 'react';

import { FaUtensils } from 'react-icons/fa';
import { components } from '../components';
import { hooks } from '../hooks';
import { svg } from '../assets/svg';

export const Search: React.FC = () => {
  const dispatch = hooks.useDispatch();
  const navigate = hooks.useNavigate();

  const [opacity, setOpacity] = useState<number>(0);
  const [searchQuery, setSearchQuery] = useState('');
  const {dishesLoading, dishes} = hooks.useGetDishes();

  hooks.useScrollToTop();
  hooks.useOpacity(setOpacity);
  hooks.useThemeColor('#F6F9F9', '#F6F9F9', dispatch);

  const filteredDishes = dishes.filter((dish: any) =>
    dish.name.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const renderHeader = (): JSX.Element => {
    return (
      <components.Header
        title='Search'
        showGoBack={true}
      />
    );
  };

  const renderSearch = (): JSX.Element => {
    return (
      <section style={{
        backgroundColor: '#ffffff',
        borderBottom: '1px solid rgba(0, 0, 0, 0.06)',
        padding: '20px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
      }}>
        <div style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          width: '100%',
        }}>
          <div style={{
            position: 'absolute',
            left: 18,
            pointerEvents: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1,
          }}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              style={{ color: '#999' }}
            >
              <circle cx="11" cy="11" r="8"></circle>
              <path d="m21 21-4.35-4.35"></path>
            </svg>
          </div>
          <input
            type='text'
            value={searchQuery}
            placeholder='Search for dishes...'
            onChange={(event) => setSearchQuery(event.target.value)}
            style={{
              width: '100%',
              padding: '16px 20px 16px 50px',
              borderRadius: 14,
              backgroundColor: '#f8f9fa',
              color: 'var(--main-color)',
              border: '1.5px solid #e9ecef',
              fontSize: 16,
              fontFamily: 'DM Sans, sans-serif',
              fontWeight: 400,
              outline: 'none',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              boxSizing: 'border-box',
            }}
            onFocus={(e) => {
              e.currentTarget.style.backgroundColor = '#ffffff';
              e.currentTarget.style.borderColor = 'var(--main-turquoise)';
              e.currentTarget.style.boxShadow = '0 0 0 4px rgba(0, 176, 185, 0.08), 0 4px 12px rgba(0, 176, 185, 0.1)';
            }}
            onBlur={(e) => {
              e.currentTarget.style.backgroundColor = '#f8f9fa';
              e.currentTarget.style.borderColor = '#e9ecef';
              e.currentTarget.style.boxShadow = 'none';
            }}
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              style={{
                position: 'absolute',
                right: 12,
                width: 32,
                height: 32,
                backgroundColor: '#f0f0f0',
                border: 'none',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                transition: 'all 0.2s ease',
                zIndex: 1,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#e0e0e0';
                e.currentTarget.style.transform = 'scale(1.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#f0f0f0';
                e.currentTarget.style.transform = 'scale(1)';
              }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ color: '#666' }}
              >
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>
          )}
        </div>
      </section>
    );
  };

  const renderDishes = (): JSX.Element => {
    if (dishesLoading) return <components.Loader />;

    if (searchQuery && filteredDishes.length === 0) {
      return (
        <section style={{
          padding: '60px 20px',
          textAlign: 'center',
        }}>
          <div style={{
            width: 120,
            height: 120,
            margin: '0 auto 24px',
            borderRadius: '50%',
            backgroundColor: '#f5f7fa',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <svg.SearchSvg />
          </div>
          <h3 style={{
            fontFamily: 'DM Sans, sans-serif',
            fontSize: 20,
            fontWeight: 700,
            color: 'var(--main-color)',
            marginBottom: 8,
            marginTop: 0,
          }}>
            No results found
          </h3>
          <p style={{
            fontFamily: 'DM Sans, sans-serif',
            fontSize: 15,
            color: '#666',
            margin: 0,
          }}>
            Try searching with different keywords
          </p>
        </section>
      );
    }

    if (!searchQuery) {
      return (
        <section style={{
          padding: '60px 20px',
          textAlign: 'center',
        }}>
          <div style={{
            width: 120,
            height: 120,
            margin: '0 auto 24px',
            borderRadius: '50%',
            backgroundColor: '#f5f7fa',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <svg.SearchSvg />
          </div>
          <h3 style={{
            fontFamily: 'DM Sans, sans-serif',
            fontSize: 20,
            fontWeight: 700,
            color: 'var(--main-color)',
            marginBottom: 8,
            marginTop: 0,
          }}>
            Start searching
          </h3>
          <p style={{
            fontFamily: 'DM Sans, sans-serif',
            fontSize: 15,
            color: '#666',
            margin: 0,
          }}>
            Enter a dish name to find what you're looking for
          </p>
        </section>
      );
    }

    return (
      <section style={{padding: '20px'}}>
        <div style={{
          marginBottom: 16,
          fontFamily: 'DM Sans, sans-serif',
          fontSize: 14,
          color: '#666',
          fontWeight: 500,
        }}>
          {filteredDishes.length} {filteredDishes.length === 1 ? 'result' : 'results'} found
        </div>
        <ul
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: 16,
            listStyle: 'none',
            margin: 0,
            padding: 0,
          }}
        >
        {filteredDishes.map((dish) => {
          return (
            <li
            key={dish.id}
            style={{
              position: "relative",
              cursor: 'pointer',
              borderRadius: 18,
              overflow: 'hidden',
              backgroundColor: '#ffffff',
              boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
              border: '1px solid rgba(0, 0, 0, 0.05)',
              transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            }}
            onClick={() => navigate(`/dish/${dish.id}`, { state: { dish } })}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-4px)';
              e.currentTarget.style.boxShadow = '0 8px 24px rgba(0, 0, 0, 0.12)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 2px 12px rgba(0, 0, 0, 0.06)';
            }}
          >
            <div style={{
              width: '100%',
              height: 160,
              position: 'relative',
              overflow: 'hidden',
              backgroundColor: '#f8f9fa',
            }}>
              {dish.image ? (
                <img
                  src={dish.image}
                  alt={dish.name}
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    display: 'block',
                  }}
                />
              ) : (
                <div
                  style={{
                    width: "100%",
                    height: "100%",
                    background: "linear-gradient(135deg, #f5f7fa 0%, #e4e7eb 100%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "relative",
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      position: "absolute",
                      width: "150%",
                      height: "150%",
                      background: "radial-gradient(circle, rgba(255,255,255,0.8) 0%, transparent 70%)",
                      top: "-25%",
                      left: "-25%",
                      opacity: 0.5,
                    }}
                  />
                  <FaUtensils
                    style={{
                      fontSize: "40px",
                      color: "#b0b8c1",
                      opacity: 0.8,
                      position: 'relative',
                      zIndex: 1,
                      filter: "drop-shadow(0 2px 4px rgba(0,0,0,0.05))",
                    }}
                  />
                </div>
              )}
              <div style={{
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                background: "linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0.4) 50%, transparent 100%)",
                padding: '12px 14px 8px',
              }}>
                <span
                  className="number-of-lines-1"
                  style={{
                    color: "#ffffff",
                    fontSize: 14,
                    fontWeight: 600,
                    fontFamily: 'DM Sans, sans-serif',
                    textShadow: "0 2px 4px rgba(0,0,0,0.5)",
                    display: 'block',
                  }}
                >
                  {dish.name}
                </span>
              </div>
            </div>
          </li>
          );
        })}
        </ul>
      </section>
    );
  };

  const renderContent = (): JSX.Element => {
    return <main className='scrollable container'>{renderDishes()}</main>;
  };

  return (
    <div
      id='screen'
      style={{opacity}}
    >
      {renderHeader()}
      {renderSearch()}
      {renderContent()}
    </div>
  );
};
